import { useVirtualizer, type VirtualItem, type Virtualizer } from '@tanstack/react-virtual';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import type { Revision, RevisionPage, RevisionTreePage } from '@gitbook/hive-core';
import { getScrollParent } from '@gitbook/spine-react';
import { useEventCallback, useResizeObserver } from '@gitbook/util-react-hooks';
import type { DiffTreeChange } from '@gitbook/util-tree-merge';
import { assert } from '@gitbook/util-ts';

import { PageDiffPanel, type PageDiffPanelProps } from './PageDiffPanel';
import type { ViewedChange } from './viewedChanges';

/**
 * Virtualized list of page diffs.
 */
export function PageDiffList(props: {
    entries: {
        key: string;
        pageDiff: DiffTreeChange<RevisionTreePage<RevisionPage>>;
    }[];
    viewedChanges: Map<string, ViewedChange>;
    baseRevision: Revision;
    currentRevision: Revision;
    changeRequest: api.ChangeRequest;
    organizationId: string;
}) {
    const { entries, baseRevision, currentRevision, changeRequest, organizationId, viewedChanges } =
        props;
    const [expandedPages, setExpandedPages] = React.useState<Set<string>>(() => {
        return entries.reduce<Set<string>>((set, entry) => {
            const pageId = entry.pageDiff.item.page.id;
            const isViewed = viewedChanges.get(pageId);
            if (!isViewed) {
                set.add(pageId);
            }
            return set;
        }, new Set());
    });

    const containerRef = React.useRef<HTMLDivElement>(null);
    const [containerOffset, setContainerOffset] = React.useState(0);
    const observe = useResizeObserver(() => {
        if (containerRef.current) {
            setContainerOffset(containerRef.current.offsetTop);
        }
    });
    React.useLayoutEffect(() => {
        if (containerRef.current) {
            const scrollParent = getScrollParent(containerRef.current);
            const intro = scrollParent.querySelector('#change-request-intro');
            assert(intro);
            observe(intro);
        }
    }, [observe]);
    const virtualizer = useVirtualizer({
        count: entries.length,
        getScrollElement() {
            return containerRef.current ? getScrollParent(containerRef.current) : null;
        },
        estimateSize() {
            return 280;
        },
        scrollMargin: containerOffset,
        getItemKey(index) {
            const item = entries[index];
            assert(item);
            return item.key;
        },
        overscan: 0,
    });

    const virtualItems = virtualizer.getVirtualItems();
    const getScrollTop = React.useCallback(() => {
        const container = containerRef.current;
        if (!container) {
            return null;
        }
        const scrollParent = getScrollParent(container);
        if (!scrollParent) {
            return null;
        }
        return scrollParent.scrollTop;
    }, []);
    return (
        <div
            ref={containerRef}
            className="relative"
            style={{
                height: virtualizer.getTotalSize(),
            }}
        >
            <div className="w-full">
                <div style={{ height: (virtualItems[0]?.start ?? 0) - containerOffset }} />
                {virtualItems.map((virtualItem) => {
                    const entry = entries[virtualItem.index];
                    assert(entry);
                    return (
                        <PageDiffPanelItem
                            key={virtualItem.key}
                            baseRevision={baseRevision}
                            currentRevision={currentRevision}
                            changeRequest={changeRequest}
                            organizationId={organizationId}
                            pageDiff={entry.pageDiff}
                            expandedPages={expandedPages}
                            setExpandedPages={setExpandedPages}
                            getScrollTop={getScrollTop}
                            virtualItem={virtualItem}
                            virtualizer={virtualizer}
                        />
                    );
                })}
            </div>
        </div>
    );
}

/**
 * Wrapper over PageDiffPanel to ensure memoization.
 */
function PageDiffPanelItem(
    props: {
        getScrollTop: () => number | null;
        expandedPages: Set<string>;
        setExpandedPages: React.Dispatch<React.SetStateAction<Set<string>>>;
        virtualItem: VirtualItem;
        virtualizer: Virtualizer<Element, Element>;
    } & Pick<
        PageDiffPanelProps,
        'baseRevision' | 'currentRevision' | 'changeRequest' | 'pageDiff' | 'organizationId'
    >
) {
    const {
        getScrollTop,
        baseRevision,
        currentRevision,
        changeRequest,
        pageDiff,
        expandedPages,
        setExpandedPages,
        virtualItem,
        virtualizer,
        organizationId,
    } = props;
    const pageId = pageDiff.item.page.id;
    const handleExpanded = useEventCallback((isExpanded: boolean) => {
        setExpandedPages((prev) => {
            if (isExpanded && !prev.has(pageId)) {
                const next = new Set(prev);
                next.add(pageId);
                return next;
            }
            if (!isExpanded && prev.has(pageId)) {
                const next = new Set(prev);
                next.delete(pageId);
                return next;
            }
            return prev;
        });
        if (!isExpanded) {
            const scrollTop = getScrollTop();
            // Compute the optimal margin by removing the height of sticky elements.
            const offset = virtualItem.start - 78 - 49;
            // If the header is sticky, scroll it to top to avoid bugs.
            if (scrollTop && scrollTop > offset) {
                virtualizer.scrollToOffset(offset, {
                    align: 'start',
                    behavior: 'auto',
                });
            }
        }
    });
    const isExpanded = expandedPages.has(pageId);
    return (
        <div
            key={virtualItem.key}
            ref={virtualizer.measureElement}
            data-index={virtualItem.index}
            className="pb-6"
        >
            <PageDiffPanel
                baseRevision={baseRevision}
                currentRevision={currentRevision}
                changeRequest={changeRequest}
                isExpanded={isExpanded}
                onExpandedChange={handleExpanded}
                organizationId={organizationId}
                pageDiff={pageDiff}
            />
        </div>
    );
}
